import GridCard from '@Guidelines/Component/GridCard';
import IdentityTile from '@Guidelines/Component/IdentityTile';
import { theme } from '@Guidelines/Theme/theme';
import UserCardActions from '@UI/Pages/Administration/Users/UserCard/UserCardActions';
import UserStatusTag from '@UI/Pages/Administration/Users/UserCard/UserStatusTag';
import formatLongDate from '@UI/Helpers/formatLongDate';
import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import type UserView from '@UI/Store/Users/Views/UserView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const USER_CARD_TRACK_COUNT = 4;

const headerRowStyle = css`
	display: flex;
	align-items: flex-start;
	gap: 0.875rem;
`;

const identityTextStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	flex: 1;
	min-width: 0;
`;

const nameStyle = (isDisabled: boolean) => css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${isDisabled ? theme.colors.inkFaint : theme.colors.ink};
`;

const emailStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	color: ${theme.colors.inkFaint};
`;

const pillsRowStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const pillStyle = css`
	padding: 0.4375rem 0.75rem;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.small};
`;

const metadataTextStyle = css`
	margin-left: 0.5rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly user: UserView;
	/** `null` while still loading — used both to resolve the role pill and to feed its own filter. */
	readonly roles: readonly RoleView[] | null;
	/**
	 * Every facility the caller is assigned to, flattened across organizations — `null` while still
	 * loading. Used only to resolve the facilities pill label; grouping by organization is the
	 * sidebar's picker's concern, not this read-only lookup's.
	 */
	readonly facilities: readonly AccountFacilityView[] | null;
};

/**
 * One account, as a grid cell: identity (tile + name/email), status, role/facility pills, an optional
 * invitation expiry line, and actions. `roles`/`facilities` mirror the same lookup — and the same
 * fallback labels — the page used to render as table columns: `Aucun rôle` / `Rôle inconnu` /
 * `Aucune agence` / `Agence inconnue`, and a `Skeleton` while the corresponding list has not arrived.
 */
const UserCard: React.FC<Props> = ({ user, roles, facilities }) => {
	const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

	const roleLabel = React.useMemo(() => {
		if (roles === null) {
			return null;
		}

		if (user.roleId === null) {
			return 'Aucun rôle';
		}

		return roles.find((role) => role.id === user.roleId)?.label ?? 'Rôle inconnu';
	}, [roles, user.roleId]);

	const facilitiesLabel = React.useMemo(() => {
		if (facilities === null) {
			return null;
		}

		if (user.facilityIds.length === 0) {
			return 'Aucune agence';
		}

		if (user.facilityIds.length > 5) {
			// show only the first 5 facilities and indicate there are more
			const facilityNames = user.facilityIds
				.slice(0, 5)
				.map(
					(facilityId) =>
						facilities.find((facility) => facility.id === facilityId)?.name ?? 'Agence inconnue',
				);

			return `${facilityNames.join(', ')} et ${user.facilityIds.length - 5} autres`;
		}

		return user.facilityIds
			.map(
				(facilityId) =>
					facilities.find((facility) => facility.id === facilityId)?.name ?? 'Agence inconnue',
			)
			.join(', ');
	}, [facilities, user.facilityIds]);

	const metadataText = React.useMemo<string | null>(() => {
		if (user.ssoLinkedAt !== null) {
			return `Lié au fournisseur le ${formatLongDate(user.ssoLinkedAt)}`;
		}

		if (user.isPendingInvitation && user.invitationExpiresAt !== null) {
			const prefix = user.isInvitationExpired ? 'Invitation expirée le ' : 'Expire le ';

			return `${prefix}${formatLongDate(user.invitationExpiresAt)}`;
		}

		return null;
	}, [user.ssoLinkedAt, user.isPendingInvitation, user.invitationExpiresAt, user.isInvitationExpired]);

	return (
		<GridCard trackCount={USER_CARD_TRACK_COUNT} dimmed={!user.isActive} asListItem>
			<div css={headerRowStyle}>
				<IdentityTile label={initials} seed={user.id} />
				<div css={identityTextStyle}>
					<p css={nameStyle(!user.isActive)}>{user.fullName}</p>
					<span css={emailStyle}>{user.email}</span>
				</div>
				<UserStatusTag user={user} />
			</div>
			<div css={pillsRowStyle}>
				{roleLabel === null ? (
					<Skeleton width="6rem" height="1rem" />
				) : (
					<span css={pillStyle}>{roleLabel}</span>
				)}
				{facilitiesLabel === null ? (
					<Skeleton width="8rem" height="1rem" />
				) : (
					<span css={pillStyle}>{facilitiesLabel}</span>
				)}
				<span css={pillStyle}>{user.authenticationMode.label}</span>
			</div>
			{/* Always rendered, even empty: this is the subgrid's third row track. Skipping it when
			there is no SSO/invitation text would drop the track for this card only, shifting its
			actions out of alignment with sibling cards that do have metadata. */}
			<span css={metadataTextStyle}>{metadataText}</span>
			<UserCardActions user={user} />
		</GridCard>
	);
};

export default UserCard;
